import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Video, MessageCircle, Users, LogOut, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [doubts, setDoubts] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, coursesRes, classesRes, doubtsRes, quizzesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, getAuthHeader()),
        axios.get(`${API}/courses`, getAuthHeader()),
        axios.get(`${API}/live-classes`, getAuthHeader()),
        axios.get(`${API}/doubts`, getAuthHeader()),
        axios.get(`${API}/tests`, getAuthHeader())
      ]);

      setStats(statsRes.data);
      const myCourses = coursesRes.data.filter(c => c.teacher_id === user.id);
      setCourses(myCourses);
      setLiveClasses(classesRes.data);
      setDoubts(doubtsRes.data);
      setQuizzes(quizzesRes.data.filter(q => q.teacher_id === user.id));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-extrabold text-slate-900">EduFlow</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Teacher Dashboard</h1>
          <p className="text-lg text-slate-600">Manage your courses and students</p>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-white border border-slate-200 p-1 gap-1">
            <TabsTrigger value="overview" data-testid="overview-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Overview</TabsTrigger>
            <TabsTrigger value="courses" data-testid="courses-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">My Courses</TabsTrigger>
            <TabsTrigger value="live-classes" data-testid="live-classes-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Live Classes</TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="quizzes-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Tests & Quizzes</TabsTrigger>
            <TabsTrigger value="doubts" data-testid="doubts-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">Doubts</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Button
                data-testid="create-course-quick-btn"
                onClick={() => navigate('/teacher/create-course')}
                className="h-20 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white flex flex-col items-center justify-center gap-2"
              >
                <Plus className="w-6 h-6" />
                <span>Create Course</span>
              </Button>
              <Button
                data-testid="create-quiz-quick-btn"
                onClick={() => navigate('/teacher/create-quiz')}
                className="h-20 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex flex-col items-center justify-center gap-2"
              >
                <Plus className="w-6 h-6" />
                <span>Create Quiz</span>
              </Button>
              <Button
                data-testid="manage-resources-quick-btn"
                onClick={() => navigate('/teacher/manage-resources')}
                className="h-20 rounded-xl bg-amber-600 hover:bg-amber-700 text-white flex flex-col items-center justify-center gap-2"
              >
                <Plus className="w-6 h-6" />
                <span>Manage Resources</span>
              </Button>
              <Button
                data-testid="view-enrollments-quick-btn"
                onClick={() => navigate('/teacher/manage-enrollments')}
                className="h-20 rounded-xl bg-purple-600 hover:bg-purple-700 text-white flex flex-col items-center justify-center gap-2"
              >
                <Users className="w-6 h-6" />
                <span>View Enrollments</span>
              </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card data-testid="total-courses-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">My Courses</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.courses || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </Card>

              <Card data-testid="live-classes-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Live Classes</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.live_classes || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Video className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
              </Card>

              <Card data-testid="pending-doubts-card" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Pending Doubts</p>
                    <p className="text-3xl font-bold text-slate-900">{stats?.pending_doubts || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">My Courses</h2>
              <Button
                data-testid="create-course-btn"
                onClick={() => navigate('/teacher/create-course')}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Course
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.length === 0 ? (
                <p className="text-slate-500 col-span-full text-center py-12">No courses created yet</p>
              ) : (
                courses.map((course) => (
                  <Card
                    key={course.id}
                    data-testid={`course-card-${course.id}`}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden card-hover"
                  >
                    <div className="h-40 bg-gradient-to-br from-indigo-500 to-teal-500"></div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{course.title}</h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{course.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-indigo-600">₹{course.price}</span>
                        <span className="text-sm text-slate-500">{course.enrolled_count} students</span>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Live Classes Tab */}
          <TabsContent value="live-classes" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Live Classes</h2>
              <Button
                data-testid="create-live-class-btn"
                onClick={() => navigate('/teacher/create-live-class')}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Class
              </Button>
            </div>
            <div className="space-y-4">
              {liveClasses.length === 0 ? (
                <p className="text-slate-500 text-center py-12">No live classes scheduled</p>
              ) : (
                liveClasses.map((liveClass) => (
                  <Card
                    key={liveClass.id}
                    data-testid={`live-class-card-${liveClass.id}`}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-bold text-slate-900">{liveClass.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            liveClass.status === 'live' ? 'bg-green-100 text-green-700' :
                            liveClass.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {liveClass.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{liveClass.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <span>📅 {new Date(liveClass.scheduled_at).toLocaleDateString()}</span>
                          <span>⏰ {new Date(liveClass.scheduled_at).toLocaleTimeString()}</span>
                          <span>⏱️ {liveClass.duration_minutes} min</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate(`/live-class/${liveClass.id}`)}
                        size="sm"
                        className="rounded-full"
                      >
                        Enter Class
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Tests & Quizzes Tab */}
          <TabsContent value="quizzes" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Tests & Quizzes</h2>
              <Button
                data-testid="create-quiz-btn"
                onClick={() => navigate('/teacher/create-quiz')}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Quiz
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quizzes.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-slate-500 mb-4">No quizzes created yet</p>
                  <Button
                    onClick={() => navigate('/teacher/create-quiz')}
                    className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Quiz
                  </Button>
                </div>
              ) : (
                quizzes.map((quiz) => (
                  <Card
                    key={quiz.id}
                    data-testid={`quiz-card-${quiz.id}`}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-teal-600" />
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        {quiz.questions?.length || 0} Questions
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{quiz.title}</h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">{quiz.description}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between text-slate-600">
                        <span>Duration:</span>
                        <span className="font-medium">{quiz.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-600">
                        <span>Total Marks:</span>
                        <span className="font-medium">{quiz.total_marks}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200 flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-full"
                        onClick={() => navigate(`/test/${quiz.id}`)}
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => toast.info('View results coming soon')}
                      >
                        View Results
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Doubts Tab */}
          <TabsContent value="doubts" className="mt-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Student Doubts</h2>
            <div className="space-y-4">
              {doubts.length === 0 ? (
                <p className="text-slate-500 text-center py-12">No doubts from students</p>
              ) : (
                doubts.map((doubt) => (
                  <Card
                    key={doubt.id}
                    data-testid={`doubt-${doubt.id}`}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{doubt.title}</h3>
                          <span className="text-xs text-slate-500">by {doubt.student_name}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{doubt.description}</p>
                        
                        {doubt.answer ? (
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-3">
                            <p className="text-sm font-medium text-teal-900 mb-1">Your Answer:</p>
                            <p className="text-sm text-teal-700">{doubt.answer}</p>
                          </div>
                        ) : (
                          <div className="mt-4">
                            <Label htmlFor={`answer-${doubt.id}`} className="text-slate-700 font-medium mb-2 block">
                              Provide Answer
                            </Label>
                            <Textarea
                              id={`answer-${doubt.id}`}
                              data-testid={`answer-input-${doubt.id}`}
                              placeholder="Type your answer here..."
                              className="mb-3 text-slate-900"
                              onBlur={async (e) => {
                                const answer = e.target.value;
                                if (answer.trim()) {
                                  try {
                                    await axios.put(
                                      `${API}/doubts/${doubt.id}/answer?answer=${encodeURIComponent(answer)}`,
                                      {},
                                      getAuthHeader()
                                    );
                                    toast.success('Answer submitted!');
                                    fetchDashboardData();
                                  } catch (error) {
                                    toast.error('Failed to submit answer');
                                  }
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                          doubt.status === 'answered'
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {doubt.status}
                      </span>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
